import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { SessionService } from '@app/core/session.service';
import { ApiService } from '@app/core/api.service';
import { type TQuadChartType } from '@app/models/quadChart.model';
import { type ITechReportRequest } from '@app/models/techReportRequest.model';
import { WorkspaceService } from '@app/core/workspace.service';
import { DbTable } from '@app/models/models.helpers';
import { SupabaseService } from './supabase.service';
import { RealtimeChannel } from '@supabase/supabase-js';
import { NavigationEnd, Router } from '@angular/router';
import { defer, distinctUntilChanged, filter, map, merge, of } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ISearchQueueFullError,
  ISearchFromTopicsEnqueueResponse,
  ISearchQueueStatus,
  StartSearchFromTopicsOutcome,
  TvDataset,
  TvManifest,
  TvModuleKey,
  TvModuleStatus,
} from '@app/models/techVector.model';
import { Database } from '@app/types/supabase';
@Injectable({
    providedIn: 'root'
})
export class TechVectorService {
    sessionService = inject(SessionService);
    api = inject(ApiService);
    workspaceService = inject(WorkspaceService);
    supabase = inject(SupabaseService);
    private router = inject(Router);

    private extractTvIdFromCurrentRoute(): string | null {
        let route = this.router.routerState.root;
        let tvId: string | null = null;
        while (route) {
            if (route.snapshot.paramMap.has('tv_id')) {
                tvId = route.snapshot.paramMap.get('tv_id');
            }
            if (route.firstChild) {
                route = route.firstChild;
            } else {
                break;
            }
        }
        return tvId;
    }

  
    private tvIdFromRoute$ = merge(
        defer(() => of(this.extractTvIdFromCurrentRoute())),
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map(() => this.extractTvIdFromCurrentRoute()),
        ),
    ).pipe(distinctUntilChanged());

    readonly activeTechVectorId = toSignal(this.tvIdFromRoute$, { initialValue: null });
    readonly activeTechVector = signal<DbTable<'tech_vectors'> | null>(null);
    readonly modules = signal<DbTable<'tv_modules'>[]>([]);

    // Imperative endpoint override. Lets a host that is NOT under a `/tv/:tv_id`
    // route (e.g. the Defense BD app embedding a TV org-profile drawer) point the
    // route-context-free `tvBaseEndpoint` / `tvBaseRoute` at a specific public TV
    // for the duration the drawer is open. It deliberately does NOT touch
    // `activeTechVectorId` (which drives realtime subscriptions) — only the
    // endpoint/route helpers the data-view components read.
    private readonly endpointOverride = signal<{ workspaceId: string; tvId: string } | null>(null);
    setEndpointOverride(workspaceId: string | null | undefined, tvId: string | null | undefined): void {
        this.endpointOverride.set(workspaceId && tvId ? { workspaceId, tvId } : null);
    }

    public readonly tvBaseRoute = computed(() => {
        const ov = this.endpointOverride();
        if (ov) return `/app/ws/${ov.workspaceId}/tv/${ov.tvId}`;
        const id = this.activeTechVectorId();
        if (!id) return '';
        return `/app/ws/${this.workspaceService.activeWorkspaceId()}/tv/${id}`;
    })
    public formatTvBaseEndpoint(workspaceId: string, tvId: string): string {
      return `ws/${workspaceId}/tv/${tvId}`;
    }
    public readonly tvBaseEndpoint = computed(() => {
        const ov = this.endpointOverride();
        if (ov) return this.formatTvBaseEndpoint(ov.workspaceId, ov.tvId);
        const id = this.activeTechVectorId();
        if (!id) return '';
        return this.formatTvBaseEndpoint(this.workspaceService.activeWorkspaceId(), id);
    })

    // True when the active TV context is the shared `public` workspace. UI should
    // suppress write/escalation actions (reports, chat, open-in-new-tab to the
    // full TV shell) for public tech vectors — viewers get read-only profiles.
    public readonly actionsLocked = computed(() => {
        const ov = this.endpointOverride();
        const ws = ov ? ov.workspaceId : this.workspaceService.activeWorkspaceId();
        return ws === 'public';
    })

    private subscription: RealtimeChannel | null = null;
    private moduleSubscription: RealtimeChannel | null = null;

    private getModuleVersionDate(module: DbTable<'tv_modules'>): number {
        const raw = (module as { version_date?: string | null }).version_date;
        if (!raw) return 0;
        const parsed = new Date(raw).getTime();
        return Number.isNaN(parsed) ? 0 : parsed;
    }

    constructor() {
        effect(() => {
            const id = this.activeTechVectorId();
            console.log('in tech-vector.service activeTechVectorId', id);

            // Clean up existing subscriptions whenever the ID changes
            this.cleanupSubscription();
            this.cleanupModuleSubscription();

            if (id) {
                // Setup subscriptions for the new ID
                this.setupRealtimeSubscription(id);
                this.setupModuleRealtimeSubscription(id);
            } else {
                // User has navigated away from any :tv_id route
                console.log('Clearing active tech vector and modules (navigated away)');
                this.activeTechVector.set(null);
                // this.activeJob.set(null);
            }
        });
    }

    private setupRealtimeSubscription(id: string): Promise<void> {
        this.cleanupSubscription();

        return new Promise((resolve, reject) => {
            this.subscription = this.supabase.client
                .channel(`tech-vector-${id}`)
                .on(
                    'postgres_changes',
                    {
                        event: '*',
                        schema: 'public',
                        table: 'tech_vectors',
                        filter: `id=eq.${id}`,
                    },
                    (payload) => {
                        console.log('Realtime tech vector update:', payload);
                        if (payload.eventType === 'DELETE') {
                            console.log('in tech-vector.service DELETE event');
                            this.activeTechVector.set(null);
                        } else {
                            console.log('in tech-vector.service UPDATE event', payload.new);
                            this.activeTechVector.set(payload.new as DbTable<'tech_vectors'>);
                        }
                    }
                )
                .subscribe((status, err) => {
                    if (status === 'SUBSCRIBED') {
                        console.log('Realtime subscription active for TV:', id);
                        resolve();
                    }
                    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                        console.error('Realtime subscription failed:', err);
                        reject(err);
                    }
                });
        });
    }

    private setupModuleRealtimeSubscription(id: string): Promise<void> {
        this.cleanupModuleSubscription();

        return new Promise((resolve, reject) => {
            this.moduleSubscription = this.supabase.client
                .channel(`tv-modules-${id}`)
                .on(
                    'postgres_changes',
                    {
                        event: 'INSERT',
                        schema: 'public',
                        table: 'tv_modules',
                        filter: `tv_id=eq.${id}`,
                    },
                    (payload) => {
                        console.log('in tech-vector.service TV Module INSERT event', payload);
                        this.modules.set([...this.modules(), payload.new as DbTable<'tv_modules'>]);
                    }
                )
                .on(
                    'postgres_changes',
                    {
                        event: 'UPDATE',
                        schema: 'public',
                        table: 'tv_modules',
                        filter: `tv_id=eq.${id}`,
                    },
                    (payload) => {
                        console.log('in tech-vector.service TV Module UPDATE event');
                        console.log(`Module Key: ${payload.new['module_key']} -- Dataset Keys: ${JSON.stringify(payload.new['dataset_keys'])} -- Status: ${payload.new['status']} -- Progress: ${payload.new['progress']}`);
                        this.modules.set(this.modules().map(m => m['id'] === payload.new['id'] ? payload.new as DbTable<'tv_modules'> : m));
                        // if new is COMPLETED, refresh manifest
                        if (payload.new?.['status'] === 'COMPLETED') {
                          this.loadManifest()
                        }
                    }
                )
                .on(
                    'postgres_changes',
                    {
                        event: 'DELETE',
                        schema: 'public',
                        table: 'tv_modules',
                        filter: `tv_id=eq.${id}`,
                    },
                    (payload) => {
                        console.log('in tech-vector.service TV Module DELETE event', payload);
                        this.modules.set(this.modules().filter(m => m['id'] !== payload.old['id']));
                    }
                )
                .subscribe((status, err) => {
                    if (status === 'SUBSCRIBED') {
                        console.log('Realtime module subscription active for TV:', id);
                        resolve();
                    }
                    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                        console.error('Realtime module subscription failed:', err);
                        reject(err);
                    }
                });
        });
    }

    private cleanupSubscription() {
        if (this.subscription) {
            console.log('Cleaning up tech vector subscription');
            this.supabase.client.removeChannel(this.subscription);
            this.subscription = null;
        }
    }

    private cleanupModuleSubscription() {
        if (this.moduleSubscription) {
            console.log('Cleaning up module subscription');
            this.supabase.client.removeChannel(this.moduleSubscription);
            this.moduleSubscription = null;
        }
    }

    setActiveTechVector(techVector: DbTable<'tech_vectors'>) {
        // If the IDs match, we just update the data. 
        // The effect will handle subscription logic if the ID actually changed.
        console.log('Setting active tech vector', techVector.id);
        this.activeTechVector.set(techVector);
    }

    /**
     * Loads a tech vector and ensures the realtime subscription is ready.
     * Returns true if successful and permitted, false otherwise.
     */
    async loadActiveTechVector(id: string): Promise<boolean> {
        try {
            // 1. Initial Fetch (Checks Permissions via RLS)
            const { data, error } = await this.supabase.client
                .from('tech_vectors')
                .select('*')
                .eq('id', id)
                .single();

            if (error) {
                if (error.code === 'PGRST116' || error.code === '42501') {
                    console.error('Permission denied or record not found for Tech Vector:', id);
                } else {
                    console.error('Error fetching Tech Vector:', error);
                }
                return false;
            }

            // 2. Fetch Initial Module States
            const { data: moduleData } = await this.supabase.client
                .from('tv_modules')
                .select('*')
                .eq('tv_id', id)
                .order('updated_at', { ascending: false });

            if (moduleData) {
                this.modules.set(moduleData);
                console.log('Initial modules', moduleData);
            }

            // 3. Set Data
            this.activeTechVector.set(data as DbTable<'tech_vectors'>);
            // this.activeModule.set(jobData as DbTable<'tv_modules'>);

            // 4. Setup and wait for realtime subscriptions and manifest priming.
            // Priming manifest here keeps nav dataset statuses correct on any child route refresh,
            // not just when the dashboard component triggers loadManifest().
            const workspaceId = (data as DbTable<'tech_vectors'>)?.workspace_id ?? this.workspaceService.activeWorkspaceId();
            await Promise.all([
                this.setupRealtimeSubscription(id),
                this.setupModuleRealtimeSubscription(id),
                this.loadManifest({ tvId: id, workspaceId }),
            ]);

            return true;
        } catch (error) {
            console.error('Unexpected error in loadActiveTechVector:', error);
            return false;
        }
    }

    // async listTechVectors(params: {
    //   start?: number;
    //   limit?: number;
    //   order_by?: 'updated_at' | 'title';
    //   order_direction?: 'asc' | 'desc';
    //   include_all?: boolean;
    // } = {}): Promise<{total_count: number, items: Partial<DbTable<'tech_vectors'>>[]}> {
    //   const res = await this.api.apost<{total_count: number, items: Partial<DbTable<'tech_vectors'>>[]}>('tech-vectors/list', params);
    //   return res;
    // }

    // async updateTechVectorTitle(tvId: string, title: string, description: string): Promise<boolean> {
    //   const res = await this.api.apost<{success: boolean}>(`tech-vectors/${tvId}/update-title`, {
    //     title,
    //     description,
    //   });
    //   return res.success;
    // }

    async deleteTechVector(tvId: string): Promise<boolean> {
        const res = await this.api.apost<{ success: boolean }>(`ws/${this.workspaceService.activeWorkspaceId()}/tv/${tvId}/archive`, {});
        return res.success;
    }

    /**
     * Enqueues search-from-topics (returns immediately with job_id / ETA).
     * Completion is tracked via tv_modules realtime updates.
     */
    async startSearchFromTopics(): Promise<StartSearchFromTopicsOutcome> {
        const endpoint = this.tvBaseEndpoint();
        if (!endpoint) {
            throw new Error('No active tech vector');
        }
        try {
            const response = await this.api.apost<ISearchFromTopicsEnqueueResponse>(
                `${endpoint}/search-from-topics`,
                null,
                { appEnv: 'search' },
            );
            return { kind: 'enqueued', response };
        } catch (err: unknown) {
            const httpErr = err as HttpErrorResponse;
            if (
                httpErr?.status === 429 &&
                httpErr.error?.error === 'search_queue_full'
            ) {
                return {
                    kind: 'queue_full',
                    error: httpErr.error as ISearchQueueFullError,
                };
            }
            throw err;
        }
    }

    async getSearchQueueStatus(): Promise<ISearchQueueStatus | null> {
        try {
            return await this.api.aget<ISearchQueueStatus>(
                'search/queue/status',
                {},
                { appEnv: 'search', noAuth: true },
            );
        } catch (err) {
            console.error('[TechVectorService] failed to load search queue status', err);
            return null;
        }
    }

    async getTechReportRequest(tvId: string): Promise<ITechReportRequest | null> {
        const res = await this.api.aget<{ create_tech_report_request: ITechReportRequest | null }>(`ws/${this.workspaceService.activeWorkspaceId()}/tv/${tvId}/tech-reports/request`);
        return res.create_tech_report_request;
    }

    getTechVectorImagePath(tvId: string, workspaceId: string): string {
      return `workspaces/${workspaceId}/tech_vectors/${tvId}/images/cover.png`;
    }

    async createTechReportRequest(tvId: string, params: {
        report_title: string;
        user_instructions: string;
    }): Promise<ITechReportRequest> {
        const res = await this.api.apost<ITechReportRequest>(`ws/${this.workspaceService.activeWorkspaceId()}/tv/${tvId}/tech-reports/request`, params);
        return res;
    }

    /**
     * Datasets
     */
    manifest = signal<TvManifest | null>(null);
    async loadManifest(params?: { tvId?: string | null; workspaceId?: string | null }) {
      const tvId = params?.tvId ?? this.activeTechVectorId();
      const workspaceId = params?.workspaceId ?? this.workspaceService.activeWorkspaceId();
      if (!tvId || !workspaceId) {
        return null;
      }
      try {
        const res = await this.api.aget<{ manifest: TvManifest }>(`ws/${workspaceId}/tv/${tvId}/dataset-manifest`);
        console.log('manifest res', res); 
        this.manifest.set(res.manifest);
        return this.manifest();
      } catch (err) {
        console.error('error loading manifest', err);
        return null;
      }
    }

    getModuleStatus(keyOrKeys: TvModuleKey | TvModuleKey[], manifest?: TvManifest | null): TvModuleStatus {
      const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys];
      const resolvedManifest = manifest ?? this.manifest();
      const allModules = this.modules() ?? [];

      // Lower rank means less complete; return the least-complete status.
      const statusRank: Record<TvModuleStatus, number> = {
        [TvModuleStatus.COMING_SOON]: 0,
        [TvModuleStatus.NOT_STARTED]: 1,
        [TvModuleStatus.FAILED]: 2,
        [TvModuleStatus.CANCELLED]: 2,
        [TvModuleStatus.CANCEL_REQUESTED]: 2,
        [TvModuleStatus.PENDING]: 3,
        [TvModuleStatus.QUEUED]: 3,
        [TvModuleStatus.RUNNING]: 4,
        [TvModuleStatus.COMPLETED]: 5,
      };

      let leastCompletedStatus = TvModuleStatus.COMPLETED;

      for (const key of keys) {
        const latestModule = allModules
          .filter(m => m.module_key === key)
          .sort((a, b) => this.getModuleVersionDate(b) - this.getModuleVersionDate(a))[0];

        if (latestModule) {
          const status = latestModule.status as TvModuleStatus;
          const normalizedStatus = statusRank[status] !== undefined ? status : TvModuleStatus.NOT_STARTED;
          if (statusRank[normalizedStatus] < statusRank[leastCompletedStatus]) {
            leastCompletedStatus = normalizedStatus;
          }
          continue;
        }

        const datasetExists = !!resolvedManifest?.datasets?.[key];
        const inferredStatus = datasetExists ? TvModuleStatus.COMPLETED : TvModuleStatus.NOT_STARTED;
        if (statusRank[inferredStatus] < statusRank[leastCompletedStatus]) {
          leastCompletedStatus = inferredStatus;
        }
      }


      return leastCompletedStatus;
    }

    /**
     * The single gate for "is this tech vector usable yet?".
     *
     * Nothing in the TV (dashboard hub, data views, chat, reports) is made
     * available until BOTH core modules have completed:
     *   1. gen_tv_from_topics — the AI search engine that builds the corpus.
     *   2. gen_tv_insights     — the synthesis that powers the Overview story.
     *
     * Module rows are the source of truth; we also accept the presence of the
     * corresponding dataset in the manifest as a fallback so older tech vectors
     * (built before a given module row existed) still resolve as ready.
     */
    readonly coreModulesReady = computed<boolean>(() => {
      // Touch the reactive sources so this recomputes on realtime updates.
      this.modules();
      this.manifest();

      const docsReady =
        this.getModuleStatus(TvModuleKey.GEN_TV_FROM_TOPICS) === TvModuleStatus.COMPLETED ||
        !!this.manifest()?.datasets?.[TvDataset.PUBS];

      const insightsReady =
        this.getModuleStatus(TvModuleKey.GEN_TV_INSIGHTS) === TvModuleStatus.COMPLETED ||
        !!this.manifest()?.datasets?.[TvDataset.TV_SYNTHESIS];

      return docsReady && insightsReady;
    });

    /**
     * Quad Charts
     */
    // async getQuadChart(tvId: string, quadChartId: string): Promise<DbTable<'quad_charts'>> {
    //     const res = await this.api.aget<DbTable<'quad_charts'>>(`ws/${this.workspaceService.activeWorkspaceId()}/tv/${tvId}/quad-charts/quad-chart/${quadChartId}`);
    //     return res;
    // }


}
